import React, { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

const C = {
  panel: "#080f1a",
  card: "#0a1628",
  border: "#f7c94855",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  gold: "#ffd84a",
  text: "#cbd5e1",
  muted: "#8a6a16",
  dim: "#3f3414",
};

const S = {
  card: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
  },
  label: {
    fontSize: 9,
    color: C.muted,
    letterSpacing: 3,
    textTransform: "uppercase",
    marginBottom: 5,
  },
  th: {
    padding: "10px 14px",
    textAlign: "right",
    color: C.gold,
    fontSize: 9,
    letterSpacing: 2,
    fontWeight: "bold",
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: "9px 14px",
    textAlign: "right",
    fontSize: 12,
    borderBottom: "1px solid #151000",
  },
};

const fmtOI = value => value != null ? Number(value || 0).toLocaleString("en-IN") : "0";
const fmtSpot = value => Number(value || 0) ? Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "-";
const fmtTime = value => value ? new Date(value).toLocaleTimeString("en-IN", { hour12: false }) : "";

async function getJson(url, options = {}) {
  const res = await fetch(url, { cache: "no-store", ...options });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data.success === false) {
    throw new Error(data.error || "NIFTY history fetch failed");
  }

  return data;
}

function SelectBox({ label, value, onChange, children, disabled }) {
  return (
    <label style={{ display: "block" }}>
      <div style={S.label}>{label}</div>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        disabled={disabled}
        style={{
          minWidth: 210,
          background: "#020202",
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          color: C.gold,
          fontFamily: "inherit",
          fontSize: 12,
          fontWeight: "bold",
          letterSpacing: 1,
          padding: "9px 12px",
        }}
      >
        {children}
      </select>
    </label>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{ ...S.card, padding: "14px 16px" }}>
      <div style={S.label}>{label}</div>
      <div style={{ fontSize: 18, color, fontWeight: "bold", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function SentimentBar({ ceVal, peVal, ceLabel, peLabel, title }) {
  const total = Number(ceVal || 0) + Number(peVal || 0);
  if (!total) return null;

  const cePct = ((Number(ceVal || 0) / total) * 100).toFixed(1);

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", ...S.label, marginBottom: 4 }}>
        <span style={{ color: C.green }}>{ceLabel}</span>
        <span style={{ color: C.muted, fontSize: 8 }}>{title}</span>
        <span style={{ color: C.red }}>{peLabel}</span>
      </div>

      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${cePct}%`, background: C.green }} />
        <div style={{ flex: 1, background: C.red }} />
      </div>
    </div>
  );
}

function getItmSentiment(summary) {
  if (!summary) return null;

  const volumeBias = summary.ceVolumeCr > summary.peVolumeCr ? 1 : summary.ceVolumeCr < summary.peVolumeCr ? -1 : 0;
  const oiBias = summary.ceOI > summary.peOI ? 1 : summary.ceOI < summary.peOI ? -1 : 0;
  const totalBias = volumeBias + oiBias;

  if (totalBias >= 1) {
    return { label: "CALL BUYERS AND PUT SELLERS DOMINANT - BULLISH", color: C.green };
  }

  if (totalBias <= -1) {
    return { label: "PUT BUYERS AND CALL SELLERS DOMINANT - BEARISH", color: C.red };
  }

  return { label: "BALANCED - NO CLEAR DOMINANCE", color: C.amber };
}

function ItmHistory({ data, indexLabel }) {
  if (!data) return null;

  const sentiment = getItmSentiment(data.summary);

  return (
    <div>
      {sentiment && (
        <div style={{ ...S.card, borderColor: `${sentiment.color}80`, padding: "16px 20px", marginBottom: 16 }}>
          <div style={S.label}>MARKET SENTIMENT - {indexLabel} ITM ANALYSIS</div>
          <div style={{ fontSize: 14, color: sentiment.color, fontWeight: "bold", letterSpacing: 2, marginBottom: 16 }}>
            {sentiment.label}
          </div>

          <SentimentBar
            ceVal={data.summary.ceVolumeCr}
            peVal={data.summary.peVolumeCr}
            ceLabel={`CE VOL ${data.summary.ceVolumeCr}`}
            peLabel={`PE VOL ${data.summary.peVolumeCr}`}
            title="VOLUME"
          />

          <SentimentBar
            ceVal={data.summary.ceOI}
            peVal={data.summary.peOI}
            ceLabel={`CE OI ${fmtOI(data.summary.ceOI)}`}
            peLabel={`PE OI ${fmtOI(data.summary.peOI)}`}
            title="OPEN INTEREST"
          />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
        <MetricCard label="CE VOLUME" value={data.summary.ceVolumeCr} color={C.green} />
        <MetricCard label="PE VOLUME" value={data.summary.peVolumeCr} color={C.red} />
        <MetricCard label="CE OPEN INTEREST" value={fmtOI(data.summary.ceOI)} color={C.green} />
        <MetricCard label="PE OPEN INTEREST" value={fmtOI(data.summary.peOI)} color={C.red} />
      </div>
    </div>
  );
}

function OiHistory({ data }) {
  if (!data) return null;

  const pcrColor = data.pcr < 0.7 ? C.red : data.pcr > 1.5 ? C.green : C.amber;
  const fallbackCalls = data.calls || [];
  const fallbackPuts = data.puts || [];
  const ceOIChange = Number(data.ceOIChange ?? fallbackCalls.reduce((sum, row) => sum + Number(row.oiChange || 0), 0));
  const peOIChange = Number(data.peOIChange ?? fallbackPuts.reduce((sum, row) => sum + Number(row.oiChange || 0), 0));
  const supports = data.supports || [...fallbackPuts].sort((a, b) => (b.oi || 0) - (a.oi || 0)).slice(0, 4);
  const resistances = data.resistances || [...fallbackCalls].sort((a, b) => (b.oi || 0) - (a.oi || 0)).slice(0, 4);

  const changeBadge = (row, side) => {
    const change = Number(row.oiChange || 0);
    const added = change > 0;
    const left = change < 0;

    return (
      <span
        style={{
          display: "inline-block",
          marginTop: 4,
          padding: "2px 7px",
          border: "1px solid currentColor",
          borderRadius: 4,
          fontSize: 10,
          fontWeight: "bold",
          color: added
            ? side === "CE" ? C.red : C.green
            : left
              ? side === "CE" ? C.green : C.red
              : C.muted,
        }}
      >
        {added ? `[+${fmtOI(change)} ADDED]` : left ? `[${fmtOI(change)} LEFT]` : "[0 CHANGE]"}
      </span>
    );
  };

  return (
    <div>
      <div style={{ ...S.card, borderColor: `${pcrColor}80`, padding: "20px", marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 24, alignItems: "center" }}>
          <div>
            <div style={S.label}>PCR RATIO</div>
            <div style={{ fontSize: 48, color: pcrColor, fontWeight: "bold", lineHeight: 1 }}>{data.pcr}</div>
            <div style={{ fontSize: 9, color: C.muted, marginTop: 4, letterSpacing: 1 }}>PE OI / CE OI</div>
          </div>

          <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 24 }}>
            <div style={S.label}>SIGNAL</div>
            <div style={{ fontSize: 18, color: pcrColor, fontWeight: "bold", letterSpacing: 2, marginBottom: 6 }}>
              {data.pcrSignal}
            </div>
            <div style={{ fontSize: 11, color: C.text, lineHeight: 1.6 }}>{data.pcrDesc}</div>
          </div>

          <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 24 }}>
            <div>
              <div style={S.label}>CALL SELLERS</div>
              <div style={{ fontSize: 18, color: C.red, fontWeight: "bold" }}>{fmtOI(data.totalCeOI)}</div>
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={S.label}>PUT SELLERS</div>
              <div style={{ fontSize: 18, color: C.green, fontWeight: "bold" }}>{fmtOI(data.totalPeOI)}</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
        <div style={{ ...S.card, padding: "14px 16px" }}>
          <div style={{ ...S.label, color: C.red }}>TOP 4 RESISTANCE CE OI</div>
          {resistances.map(row => (
            <div key={`res-${row.strike}`} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #151000" }}>
              <span style={{ color: C.red, fontWeight: "bold" }}>{row.strike}</span>
              <div style={{ textAlign: "right" }}>
                <div>{fmtOI(row.oi)}</div>
                {changeBadge(row, "CE")}
              </div>
            </div>
          ))}
        </div>

        <div style={{ ...S.card, padding: "14px 16px" }}>
          <div style={{ ...S.label, color: C.green }}>TOP 4 SUPPORT PE OI</div>
          {supports.map(row => (
            <div key={`sup-${row.strike}`} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #151000" }}>
              <span style={{ color: C.green, fontWeight: "bold" }}>{row.strike}</span>
              <div style={{ textAlign: "right" }}>
                <div>{fmtOI(row.oi)}</div>
                {changeBadge(row, "PE")}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...S.card, padding: "16px 20px", marginBottom: 14 }}>
        <div style={S.label}>OI CHANGE DIRECTION</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <MetricCard label="CE OI CHANGE" value={`${ceOIChange >= 0 ? "+" : ""}${fmtOI(ceOIChange)}`} color={ceOIChange >= 0 ? C.green : C.red} />
          <MetricCard label="PE OI CHANGE" value={`${peOIChange >= 0 ? "+" : ""}${fmtOI(peOIChange)}`} color={peOIChange >= 0 ? C.green : C.red} />
        </div>
      </div>
    </div>
  );
}

export default function SecretExpiryNiftyHistory({
  indexKey = "nifty",
  indexLabel = "NIFTY",
} = {}) {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedSnapshot = useMemo(
    () => snapshots.find(item => item.id === selectedId) || snapshots[snapshots.length - 1] || null,
    [snapshots, selectedId]
  );

  const loadDates = useCallback(async () => {
    const data = await getJson(`${API_BASE}/api/secret-expiry-history/${indexKey}/dates?t=${Date.now()}`);
    setDates(data.dates || []);
    setSelectedDate(prev => prev || data.dates?.[0] || "");
  }, [indexKey]);

  const loadSnapshots = useCallback(async date => {
    if (!date) {
      setSnapshots([]);
      setSelectedId("");
      return;
    }

    const data = await getJson(`${API_BASE}/api/secret-expiry-history/${indexKey}/${date}?t=${Date.now()}`);
    const rows = data.snapshots || [];
    setSnapshots(rows);
    setSelectedId(prev => rows.some(row => row.id === prev) ? prev : rows[rows.length - 1]?.id || "");
  }, [indexKey]);

  useEffect(() => {
    setLoading(true);
    loadDates()
      .catch(err => setError(err.message || "NIFTY history dates failed"))
      .finally(() => setLoading(false));
  }, [loadDates]);

  useEffect(() => {
    setLoading(true);
    loadSnapshots(selectedDate)
      .catch(err => setError(err.message || "NIFTY history snapshots failed"))
      .finally(() => setLoading(false));
  }, [selectedDate, loadSnapshots]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadDates().catch(() => {});
      if (selectedDate) loadSnapshots(selectedDate).catch(() => {});
    }, 30000);

    return () => clearInterval(timer);
  }, [loadDates, loadSnapshots, selectedDate]);

  return (
    <div>
      <div style={{ ...S.card, background: C.panel, padding: "14px 16px", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <SelectBox label="DATE" value={selectedDate} onChange={setSelectedDate} disabled={loading || !dates.length}>
            {!dates.length && <option value="">NO DATA</option>}
            {dates.map(date => <option key={date} value={date}>{date}</option>)}
          </SelectBox>

          <SelectBox label="5 MIN DATA" value={selectedId} onChange={setSelectedId} disabled={loading || !snapshots.length}>
            {!snapshots.length && <option value="">NO SNAPSHOTS</option>}
            {snapshots.map(item => (
              <option key={item.id} value={item.id}>
                {item.intervalLabel || item.time?.slice(0, 5) || fmtTime(item.capturedAt)} | SPOT {fmtSpot(item.liveSpot)}
              </option>
            ))}
          </SelectBox>

          <div
            style={{
              background: "#020202",
              border: `1px solid ${C.border}`,
              color: C.green,
              padding: "10px 18px",
              borderRadius: 4,
              fontFamily: "inherit",
              letterSpacing: 2,
              fontWeight: "bold",
              fontSize: 11,
            }}
          >
            AUTO SAVE 5 MIN
          </div>

          <div style={{ marginLeft: "auto", textAlign: "right" }}>
            {selectedSnapshot && (
              <>
                <div style={S.label}>LIVE SPOT / ANALYSIS SPOT</div>
                <div style={{ color: C.gold, fontSize: 18, fontWeight: "bold" }}>
                  {fmtSpot(selectedSnapshot.liveSpot)} / {fmtSpot(selectedSnapshot.analysisSpot)}
                </div>
                <div style={{ color: C.muted, fontSize: 10, marginTop: 4 }}>
                  EXPIRY {selectedSnapshot.expiry} | {selectedSnapshot.intervalLabel || fmtTime(selectedSnapshot.capturedAt)}
                </div>
              </>
            )}
            {loading && <div style={{ color: C.amber, fontSize: 11, letterSpacing: 2 }}>LOADING HISTORY...</div>}
            {error && <div style={{ color: C.red, fontSize: 11 }}>{error}</div>}
          </div>
        </div>
      </div>

      {!selectedSnapshot && !loading && (
        <div style={{ textAlign: "center", padding: "100px 0", color: C.dim }}>
          <div style={{ fontSize: 11, letterSpacing: 4, color: C.muted }}>WAITING FOR {indexLabel} 5 MIN HISTORY</div>
        </div>
      )}

      {selectedSnapshot && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 18 }}>
          <ItmHistory data={selectedSnapshot.itm} indexLabel={indexLabel} />
          <OiHistory data={selectedSnapshot.oi} />
        </div>
      )}
    </div>
  );
}
